import { useId, useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import useIsDesktop from './useIsDesktop';

/**
 * The "Why we ask & what you get" explainer: a modal bottom sheet on phones, an inline
 * expanding panel on desktop.
 *
 * WHY THIS IS BUILT THE HARD WAY. CLAUDE.md records that this page previously had a Tooltip
 * component, and that it was deleted for being mouse-only: it appeared on hover, so it did not
 * exist for anyone using a keyboard, a screen reader, or a touchscreen. Replacing it with
 * anything less than a real button plus real focus management would reintroduce the same defect
 * with nicer styling, so the keyboard path is built and tested first here rather than added
 * afterwards.
 *
 * The trigger is a <button> with aria-expanded and aria-controls, so its state is announced
 * before it is pressed. The sheet is role="dialog" aria-modal, traps Tab, closes on Escape,
 * locks body scroll, and returns focus to the trigger on close. The desktop panel deliberately
 * does NONE of that: it is not modal, so trapping focus in it would be a bug rather than a
 * feature. See useIsDesktop for why that split is JavaScript and not a media query.
 *
 * KNOWS NOTHING ABOUT PRICES. Action labels arrive with `priceLabel` already resolved by the
 * caller from the cart. This component never formats or looks up an amount, which is what keeps
 * the sheet from becoming a second place that states a price.
 *
 * IT OWNS THE SUMMARY ROW. The caller passes its heading content as `summary` and this renders
 * the row, appending the trigger to the end of it -- the <details>/<summary> shape. The trigger
 * has to sit on the question's own line, but the panel has to expand below the whole row, and a
 * component cannot place two siblings into a parent it does not control. Owning the row is the
 * alternative to lifting `open` into every caller and re-implementing the focus handling there.
 *
 * @param {object} props
 * @param {object} props.info                   entry from checkout/content/questionInfo
 * @param {React.ReactNode} props.summary       heading content; the trigger renders after it
 * @param {Array}  props.actions                [{ answerId, label, variant, priceLabel }]; the
 *                                              SHEET renders these, the desktop panel does not
 * @param {Function} props.onAction             (answerId) => void; also closes
 * @param {string} [props.describedBy]          id of the question title, so a screen reader user
 *                                              who tabs straight to the trigger hears which
 *                                              question it belongs to
 * @param {string} [props.className]            wrapper classes
 */
export default function InfoSheet({
  info,
  summary,
  actions = [],
  onAction,
  describedBy,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const isDesktop = useIsDesktop();

  const panelId = useId();
  const headingId = useId();
  const triggerRef = useRef(null);
  const surfaceRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    // Focus must come back to the trigger. Without this, closing the sheet drops focus to
    // <body> and a keyboard user restarts from the top of the document.
    triggerRef.current?.focus();
  }, []);

  /*
    Escape, focus trap and scroll lock apply ONLY to the modal sheet. Bound in one effect
    because they share the same lifetime, and skipped entirely on desktop where the panel is
    just part of the page.
  */
  useEffect(() => {
    if (!open || isDesktop) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = surfaceRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      // Wrap at both ends. Only the two boundary cases need handling; everything between them
      // is the browser's own tab order, which is already correct.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Restore rather than clear: something else may legitimately have been holding a lock.
      document.body.style.overflow = previousOverflow;
    };
  }, [open, isDesktop, close]);

  /*
    Move focus INTO the sheet when it opens. Without this the sheet is visible but focus is
    still on the trigger behind the overlay, so the first Tab lands somewhere unexpected and a
    screen reader never enters the dialog.
  */
  useEffect(() => {
    if (!open || isDesktop) return;
    surfaceRef.current?.querySelector('button')?.focus();
  }, [open, isDesktop]);

  const handleAction = (answerId) => {
    onAction?.(answerId);
    close();
  };

  const trigger = (
    <button
      type="button"
      ref={triggerRef}
      onClick={() => (open ? close() : setOpen(true))}
      aria-expanded={open}
      aria-controls={panelId}
      // "Why we ask & what you get, button" is meaningless on its own, and a keyboard user can
      // land here without having read the question above it.
      aria-describedby={describedBy}
      /*
        `basis-full sm:basis-auto` is what drops this onto its own line on a phone: a full-basis
        flex item always starts a new row. Below 640px the question already competes with an icon,
        and any question longer than the shortest would squeeze the word off the line.

        `ml-7 sm:ml-0` indents the wrapped line to the question's text column rather than the
        icon's, so the header still reads as one column.

        NOT the same breakpoint as the sheet/panel split, which is `lg` and lives in useIsDesktop.
        Between 640 and 1024 the trigger sits on the title line and opens a SHEET. Two independent
        responsive behaviours, deliberately -- do not collapse them onto one breakpoint.

        44px minimum target: this sits between a question and its answers, where a mis-tap selects
        an answer the customer did not choose.
      */
      className="ml-7 inline-flex min-h-[44px] basis-full items-center gap-1.5 self-start text-sm text-gray-600 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 sm:ml-0 sm:basis-auto"
    >
      {/*
        Quiet, and that is the point. This was 14px semibold in accent orange while the question
        beside it was near-black -- colour wins, so the eye landed on the secondary action first.
        Grey regular with an underline demotes it below the question it belongs to.

        ONE chevron that rotates, rather than swapping chevronDown for chevronUp. Swapping the
        glyph cannot be transitioned -- it is a different path -- so the indicator snapped while
        the panel beneath it animated. Rotating a single icon lets the arrow travel with the
        panel, and unlike the panel it can animate in BOTH directions because the icon is never
        unmounted.
      */}
      <Icon
        name="chevronDown"
        className={`h-3.5 w-3.5 transition-transform duration-200 ease-out ${
          open ? 'rotate-180' : ''
        }`}
      />
      <span className="border-b border-zinc-300 pb-px">Details</span>
    </button>
  );

  /*
    THE ACTION BUTTONS ARE SHEET-ONLY, and the asymmetry is deliberate.

    They exist because the sheet COVERS the answer rows: without them a customer on a phone reads
    the explanation and then has to dismiss the sheet to act, so the buttons are the only way to
    answer from inside it. Inline on desktop the rows never leave the screen, so the same two
    choices would appear twice a couple of centimetres apart -- and as large accent buttons they
    would become the loudest thing in the card, which is the problem quieting the trigger set out
    to fix.

    `actions` is still passed in both cases; this branch decides whether they render, alongside
    the focus trap, scroll lock and Escape handling that already differ by presentation.
  */
  const body = (
    <>
      <p className="text-sm text-gray-700">{info.intro}</p>

      {/*
        THE PANEL IS INTRO THEN ANSWERS, and nothing in between. It previously also rendered a
        labelled bullet list of inclusions, an accent-bordered consequence block ("If you skip
        this" / "Why it matters" / "Worth knowing"), and a gray "Nothing is charged today" box.

        All of it sat ABOVE the answer buttons, which on a phone meant the customer had to scroll
        a modal sheet to reach the choice the sheet exists to offer. Reintroducing any of it means
        re-adding both the markup here and the field in questionInfo.js, which has a test
        asserting those fields stay gone.
      */}
      {!isDesktop && actions.length > 0 && (
        <div className="mt-4 flex flex-col gap-2.5">
          {actions.map((action) => (
            <button
              key={action.answerId}
              type="button"
              onClick={() => handleAction(action.answerId)}
              className={`min-h-[48px] rounded-sm px-5 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
                action.variant === 'primary'
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'border border-zinc-300 bg-white text-gray-900 hover:bg-gray-50'
              }`}
            >
              {/*
                The price is appended from the resolved cart, never baked into the label. The
                mockups wrote "Yes, add stock photos - $98" as one string; that would keep
                quoting $98 after the catalog changed.
              */}
              {action.priceLabel ? `${action.label}, ${action.priceLabel}` : action.label}
            </button>
          ))}
        </div>
      )}
    </>
  );

  // The summary row, shared by both presentations: caller content, then the trigger.
  const summaryRow = (
    <div className="flex flex-wrap items-center gap-x-2.5">
      {summary}
      {trigger}
    </div>
  );

  if (isDesktop) {
    return (
      <div className={className}>
        {summaryRow}
        {open && (
          /*
            Not a dialog, not modal, no trap. It is an ordinary disclosure region, and
            `aria-controls` on the trigger above is what ties them together.

            NO HEADING. The sheet needs one because it covers the page and has to say what it is;
            inline, the question sits two lines above and "Details" says the rest. `ml-7` puts the
            prose in the question's text column so the icon's gutter runs the height of the header.
          */
          <div id={panelId} className="ml-7 mt-2.5 animate-panel-in">
            <div className="border-l-2 border-primary-200 pl-3">{body}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      {summaryRow}

      {open &&
        /*
          Portalled to <body>. A position:fixed sheet rendered in place would be positioned
          against any transformed ancestor instead of the viewport, and clipped by any ancestor
          with overflow hidden -- both of which the sticky summary column can introduce.
        */
        createPortal(
          <div className="fixed inset-0 z-50">
            {/*
              The overlay is a button so pointer AND keyboard users can dismiss by the same
              affordance. aria-hidden because Escape and the explicit close button are the
              announced routes out; a screen reader does not need "close, button" twice.
            */}
            <button
              type="button"
              aria-hidden="true"
              tabIndex={-1}
              onClick={close}
              className="absolute inset-0 w-full cursor-default bg-gray-900/45 animate-overlay-in"
            />

            <div className="absolute inset-x-0 bottom-0 flex max-h-[88%] flex-col">
              <div
                ref={surfaceRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={headingId}
                id={panelId}
                className="flex max-h-full flex-col border-t border-zinc-300 bg-white motion-safe:animate-sheet-in"
              >
                <div className="flex items-start gap-3 border-b border-gray-200 px-4 pb-3 pt-4">
                  <div className="flex-1">
                    <h3
                      id={headingId}
                      className="text-xl font-semibold tracking-heading text-gray-900"
                    >
                      {info.heading}
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={close}
                    className="-mr-2 -mt-2 flex h-11 w-11 flex-shrink-0 items-center justify-center text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  >
                    <Icon name="close" title="Close" className="h-5 w-5" />
                  </button>
                </div>

                <div className="overflow-y-auto px-4 pb-6 pt-4">{body}</div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
